import { isEmpty } from 'ramda';
import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import getKenmerkElementId from '~/components/page/Apps/Realworks/util/getKenmerkElementId';
import { isValid } from '~/util/Validation/Tag';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import DebouncedInput from '~/components/molecule/DebouncedInput';
import { RealworksRelatieSyncType } from '~/graphql/types';
import { Label } from '~/components/atom/Typography';

const text = {
  tagFieldPlaceholder: 'Alleen kleine letters, cijfers en (-) zijn toegestaan',
  errorLabel: 'Vul een geldige tag in',
};

type Attribute = {
  id: number;
  description: string | null;
  tag: string | null;
  checked: boolean;
};

export type Props = {
  row: Attribute;
  dataTestId?: string;
  className?: string;
  selectable: boolean;
  syncType: RealworksRelatieSyncType;
  isHighlighted?: boolean;
  highlightSubstring?: string;
  onChange: (value: string | boolean | null, field: string) => void;
};

const KenmerkRow: React.FCC<Props> = ({
  dataTestId,
  row: { id, description, tag, checked },
  onChange,
  syncType,
  selectable,
  isHighlighted = false,
  highlightSubstring,
}) => {
  const cachedValue = useRef(tag).current || '';
  const [hasError, setHasError] = useState(false);

  const onTagChange = value => {
    setHasError(!isEmpty(value) && !isValid(value));
    onChange(value, 'tag');
  };

  const onCheckboxChange = useCallback(
    () => onChange(!checked, 'checked'),
    [checked, onChange],
  );

  const externalErrors = hasError ? [text.errorLabel] : undefined;
  const hideInput =
    syncType === RealworksRelatieSyncType.FullExcludingKenmerken && checked;

  const appearance = (() => {
    switch (syncType) {
      case RealworksRelatieSyncType.FullExcludingKenmerken:
        return 'danger';
      case RealworksRelatieSyncType.LimitToKenmerken:
        return 'success';
      default:
        return undefined;
    }
  })();

  return (
    <React.Fragment>
      <HighlightedContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
        dataTestId={dataTestId}
        data-objectid={id}
        id={getKenmerkElementId(id)}
        $isHighlighted={isHighlighted}
      >
        <AnimatedCheckbox
          label={
            <Label
              inline
              fontWeight="semiBold"
              size="base"
              highlight={highlightSubstring}
            >
              {description ?? ''}
            </Label>
          }
          value={checked}
          selectable={selectable}
          onChange={onCheckboxChange}
          name="row-checkbox"
          icon={
            syncType === RealworksRelatieSyncType.FullExcludingKenmerken
              ? 'cross'
              : undefined
          }
          appearance={appearance}
        />
      </HighlightedContainer>

      <HighlightedContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
        $isHighlighted={isHighlighted}
      >
        {!hideInput && (
          <DebouncedInput
            defaultValue={cachedValue}
            onDebouncedChange={onTagChange}
            placeholder={text.tagFieldPlaceholder}
            externalErrors={externalErrors}
            name="row-input-field"
            width="100%"
          />
        )}
      </HighlightedContainer>
    </React.Fragment>
  );
};

const HighlightedContainer = styled(JustificationContainer)<{
  $isHighlighted: boolean;
}>(
  ({ theme, $isHighlighted }) => css`
    padding: ${theme.space('xxs')};
    border-radius: ${theme.getTokens().border.radius.base};
    transition: background-color 0.3s ease;

    ${$isHighlighted &&
    css`
      background-color: ${theme.color('accent', 'translucent')};
    `}
  `,
);

export default KenmerkRow;
